import React from 'react';
import { Link } from 'react-router-dom';

// The server hands out absolute links. Inside the app only the path and the
// query are wanted, so a tab moves without reloading the page.
export const inAppPath = (url) => {
  try {
    const parsed = new URL(url, window.location.origin);
    return parsed.pathname + parsed.search;
  } catch (e) {
    return null;
  }
};

/**
 * Resolves each tab's `url` (from the server) to an in-app `path`, and drops
 * a form the booking does not have yet, since it has no page to open.
 */
export const withTabPaths = (tabs) => tabs
  .map((tab) => ({ ...tab, path: tab.path || (tab.url && inAppPath(tab.url)) }))
  .filter((tab) => tab.path);

/**
 * The bar of links between a booking's forms and its notes, shared by the
 * customer and the professional apps.
 *
 * `tabs` is `{ key, label, short, icon, path }` per tab (see withTabPaths);
 * `icon` is a component, since each app ships its own icon set. `classPrefix`
 * keeps each app's own look: `booking-tabs` gives `booking-tabs__tab` and so
 * on. A single tab is no choice, so nothing is drawn.
 *
 * Links, not tabs: each one is its own page, so it can be opened in a new
 * tab, and the current one is marked as the page.
 */
const BookingTabs = ({ tabs, active, classPrefix }) => {
  if (tabs.length < 2) return null;
  const c = (part) => `${classPrefix}__${part}`;
  return React.createElement(
    'nav', { className: classPrefix, 'aria-label': 'Booking forms' },
    React.createElement('div', { className: c('group') }, tabs.map(({
      key, label, short, icon: Icon, path,
    }) => React.createElement(Link, {
      key,
      to: path,
      'aria-current': key === active ? 'page' : undefined,
      'aria-label': label,
      className: `${c('tab')}${key === active ? ' is-active' : ''}`,
      onClick: (e) => key === active && e.preventDefault(),
    },
    Icon && React.createElement(Icon, { className: c('icon') }),
    React.createElement('span', { className: c('label') }, label),
    React.createElement('span', {
      className: c('short'), 'aria-hidden': 'true',
    }, short)))),
  );
};

export default BookingTabs;
